import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User as UserIcon, LogOut, Coins, Crown, ArrowLeft, Loader2, FileText, Trash2, Mail, Share2, Eye } from "lucide-react";
import { useAuth, authAxios } from "@/context/AuthContext";
import ProposalDetail from "@/components/ProposalDetail";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Profile() {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [proposals, setProposals] = useState([]);
  const [loadingP, setLoadingP] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!loading && !user) navigate("/", { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await authAxios.get(`${API}/me/proposals?limit=100`);
        setProposals(r.data.items || []);
      } finally {
        setLoadingP(false);
      }
    })();
  }, [user]);

  const del = async (id) => {
    if (!window.confirm("حذف هذا العرض؟")) return;
    try {
      await authAxios.delete(`${API}/me/proposals/${id}`);
      setProposals((xs) => xs.filter((x) => x.id !== id));
    } catch {}
  };

  if (loading || !user) {
    return (
      <div dir="rtl" className="min-h-screen bg-[#182138] text-white flex items-center justify-center font-arabic">
        <Loader2 className="h-6 w-6 animate-spin text-[#18c98f]" />
      </div>
    );
  }

  return (
    <div dir="rtl" className="min-h-screen bg-[#182138] text-white font-arabic" data-testid="profile-page">
      <header
        className="border-b border-[#2e3a5c] bg-[#1f2a47] sticky top-0 z-20"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-3 flex-wrap">
          <button
            onClick={() => navigate("/")}
            data-testid="profile-back"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-[#182138] border border-[#2e3a5c] hover:border-[#18c98f]/60"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            عودة للحاسبة
          </button>
          <h1 className="text-base sm:text-lg font-extrabold text-[#18c98f]">حسابي في برولانسر</h1>
          <button
            onClick={async () => { await logout(); navigate("/"); }}
            data-testid="profile-logout"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-[#182138] border border-[#2e3a5c] hover:border-red-500/60 text-red-300"
          >
            <LogOut className="h-3.5 w-3.5" />
            خروج
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Identity */}
        <section className="rounded-3xl bg-[#1f2a47] border border-[#2e3a5c] p-5 flex items-center gap-4 flex-wrap">
          <div className="h-16 w-16 rounded-full bg-[#18c98f]/15 border border-[#18c98f]/40 flex items-center justify-center overflow-hidden">
            {user.picture ? (
              <img src={user.picture} alt={user.name || user.email} className="h-full w-full object-cover" />
            ) : (
              <UserIcon className="h-7 w-7 text-[#18c98f]" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-lg font-extrabold text-white truncate" data-testid="profile-name">
              {user.name || user.email.split("@")[0]}
            </div>
            <div className="text-xs text-white/60 flex items-center gap-1.5" dir="ltr">
              <Mail className="h-3 w-3" /> {user.email}
            </div>
            <div className="mt-2 flex gap-1.5 flex-wrap">
              {(user.providers || []).map((p) => (
                <span key={p} className="rounded-full bg-[#182138] border border-[#2e3a5c] px-2 py-0.5 text-[10px] text-white/70">
                  {p === "google" ? "Google" : "البريد"}
                </span>
              ))}
              {user.role === "admin" && (
                <span className="rounded-full bg-amber-400/15 border border-amber-400/40 text-amber-300 px-2 py-0.5 text-[10px] font-bold">
                  أدمن
                </span>
              )}
            </div>
          </div>
        </section>

        {/* Credits + subscription */}
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-2xl bg-[#1f2a47] border border-[#2e3a5c] p-5">
            <div className="flex items-center gap-2 text-xs text-white/60">
              <Coins className="h-4 w-4 text-[#18c98f]" />
              الرصيد المدفوع
            </div>
            <div className="mt-2 font-mono-num text-3xl font-extrabold text-[#18c98f]" data-testid="profile-credits">
              {user.paid_credits ?? 0}
            </div>
            <p className="mt-1 text-[11px] text-white/50">اقتراحات AI جاهزة للاستخدام (إضافة للمجاني الشهري ٥).</p>
          </div>
          <div className="rounded-2xl bg-[#1f2a47] border border-[#2e3a5c] p-5">
            <div className="flex items-center gap-2 text-xs text-white/60">
              <Crown className="h-4 w-4 text-amber-400" />
              الاشتراك
            </div>
            {user.sub_active ? (
              <>
                <div className="mt-2 text-xl font-extrabold text-amber-400">نشط ✨</div>
                {user.sub_expires_at && (
                  <p className="mt-1 text-[11px] text-white/60">ينتهي: {new Date(user.sub_expires_at).toLocaleDateString("ar-SA")}</p>
                )}
              </>
            ) : (
              <>
                <div className="mt-2 text-xl font-extrabold text-white/80">غير نشط</div>
                <p className="mt-1 text-[11px] text-white/50">احصل على اشتراك شهري ٩٩ ر.س لاستخدام AI بلا حدود.</p>
              </>
            )}
          </div>
        </section>

        {/* Proposals history */}
        <section>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="text-sm font-extrabold text-white/90 inline-flex items-center gap-1.5">
              <FileText className="h-4 w-4 text-[#18c98f]" />
              عروض الأسعار المحفوظة ({proposals.length})
            </h3>
          </div>
          {loadingP ? (
            <div className="rounded-2xl bg-[#1f2a47] border border-[#2e3a5c] p-6 text-center">
              <Loader2 className="h-5 w-5 animate-spin text-[#18c98f] mx-auto" />
            </div>
          ) : proposals.length === 0 ? (
            <div className="rounded-2xl bg-[#1f2a47] border border-[#2e3a5c] p-6 text-center text-white/50 text-sm">
              لم تحفظ أي عرض سعر حتى الآن. اضغط "حفظ العرض" من الحاسبة بعد توليد السعر.
            </div>
          ) : (
            <ul className="rounded-2xl bg-[#1f2a47] border border-[#2e3a5c] divide-y divide-[#2e3a5c]">
              {proposals.map((p) => (
                <li
                  key={p.id}
                  className="p-4 flex items-start justify-between gap-3 flex-wrap cursor-pointer hover:bg-[#1b2b4d] transition-colors"
                  data-testid={`proposal-row-${p.id}`}
                  onClick={() => setSelected(p)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-extrabold text-white truncate">{p.title}</div>
                    <div className="mt-1 text-xs text-white/60 flex gap-3 flex-wrap">
                      {p.service && <span>{p.service}</span>}
                      <span>{new Date(p.created_at).toLocaleString("ar-SA")}</span>
                      {p.result?.recommended_price != null && (
                        <span className="text-[#18c98f] font-bold">
                          موصى: {p.result.recommended_price}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); setSelected(p); }}
                      data-testid={`proposal-view-${p.id}`}
                      className="p-2 rounded-full bg-[#182138] border border-[#2e3a5c] text-[#18c98f] hover:border-[#18c98f]"
                      title="عرض وتفاصيل"
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); del(p.id); }}
                      data-testid={`proposal-delete-${p.id}`}
                      className="p-2 rounded-full bg-[#182138] border border-[#2e3a5c] text-red-400 hover:border-red-500"
                      title="حذف"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>

      <ProposalDetail
        proposal={selected}
        onClose={() => setSelected(null)}
        onDelete={async (id) => {
          try {
            await authAxios.delete(`${API}/me/proposals/${id}`);
            setProposals((xs) => xs.filter((x) => x.id !== id));
            setSelected(null);
          } catch {}
        }}
      />
    </div>
  );
}
